import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type {
  TransactionAgentRecord,
  TransactionQualification,
} from '../../../@types/Company';
import type { Subcontractor } from '../../../@types/Subcontractor';
import { setSelectedCompany } from '../../../store/AuthReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { showToast } from '../../common/Notification';
import { SetupPage, updateSetupState } from '../../common/SetupPage';
import Spinner from '../../common/Spinner';
import type { TransactionAgent } from './components/AgentsSection';
import AgentsSection from './components/AgentsSection';
import Step1CarteT from './components/Step1CarteT';
import Step2Mandates from './components/Step2Mandates';
import Step3AgenciesCount from './components/Step3AgenciesCount';
import Step4AgenciesList from './components/Step4AgenciesList';

// Employees and agents local state shapes (no API persistence for now)

interface TransactionQuestionnaireProps {
  hideTitle?: boolean;
  hideIntro?: boolean;
  hideActions?: boolean;
}

const TransactionQuestionnaire: React.FC<TransactionQuestionnaireProps> = ({ hideTitle = false, hideIntro = false, hideActions = false }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);

  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const existingQualification = company?.transactionQualification ?? null;
  const [formData, setFormData] = useState<TransactionQualification>({
    hasCarteT: existingQualification?.hasCarteT ?? null,
    willSignMandates: existingQualification?.willSignMandates ?? null,
    partnerAgenciesCount: existingQualification?.partnerAgenciesCount ?? null,
    transactionEmployees: existingQualification?.transactionEmployees ?? [],
    transactionAgents: existingQualification?.transactionAgents ?? [],
    gestionLocativeEmployees: existingQualification?.gestionLocativeEmployees ?? [],
  });

  // Subcontractors (partner agencies)
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [siretInput, setSiretInput] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [hasAgents, setHasAgents] = useState<boolean>((formData.transactionAgents?.length ?? 0) > 0);
  const [agentCount, setAgentCount] = useState<number>(formData.transactionAgents?.length ?? 0);
  const [agents, setAgents] = useState<TransactionAgent[]>(
    (formData.transactionAgents ?? []).map((entry: TransactionAgentRecord) => ({
      userId: typeof entry?.userId === 'number' ? entry.userId : null,
    })),
  );

  // Generic reduction confirmation state
  const [pendingReduction, setPendingReduction] = useState<{
    section: 'agents';
    reason: 'count' | 'toggle';
    nextCount: number;
    previousCount: number;
  } | null>(null);
  const [showReductionDialog, setShowReductionDialog] = useState(false);

  const buildQualificationPayload = (partial: Partial<TransactionQualification> = {}): TransactionQualification => ({
    hasCarteT: partial.hasCarteT ?? formData.hasCarteT ?? null,
    willSignMandates: partial.willSignMandates ?? formData.willSignMandates ?? null,
    partnerAgenciesCount: partial.partnerAgenciesCount ?? formData.partnerAgenciesCount ?? null,
    transactionEmployees: formData.transactionEmployees ?? [],
    transactionAgents: partial.transactionAgents
      ?? agents
        .filter((agent) => typeof agent.userId === 'number' && !Number.isNaN(agent.userId))
        .map((agent) => ({ userId: agent.userId as number })),
    gestionLocativeEmployees: formData.gestionLocativeEmployees ?? [],
  });

  // (Optional) derive labels

  // Fetch subcontractors from API
  const fetchSubcontractors = async() => {
    if (!company?.id) {return;}
    try {
      setStatus('PENDING');
      const response = await fetch(`/api/companies/${company.id}/subcontractors`, {
        headers: { 'Authorization': JWTAuthorization(jwt) },
      });
      if (!response.ok) { throw new Error('Failed to fetch subcontractors'); }
      const data = await response.json();

      const enrichedSubcontractors = await Promise.all(
        data.map(async(subcontractor: Subcontractor) => {
          try {
            const siren = subcontractor.siret.substring(0, 9);
            const siegeResponse = await fetch(`/api/sirens/${siren}`, { headers: { 'Authorization': JWTAuthorization(jwt) } });
            if (!siegeResponse.ok) { throw new Error('Failed to get headquarters information'); }
            const siegeData = await siegeResponse.json();

            const siretResponse = await fetch(`/api/sirens/${subcontractor.siret}`, { headers: { 'Authorization': JWTAuthorization(jwt) } });
            if (!siretResponse.ok) { throw new Error('Failed to get establishment information'); }
            const siretData = await siretResponse.json();

            const companyName =
              siegeData.denominationUsuelleEtablissement ||
              siegeData.enseigne1Etablissement ||
              siretData.denominationUsuelleEtablissement ||
              siretData.enseigne1Etablissement ||
              siegeData.denominationUniteLegale ||
              siretData.denominationUniteLegale ||
              siegeData.nomUniteLegale ||
              siretData.nomUniteLegale ||
              'Nom non disponible';

            const formattedStreet = `${siegeData.numeroVoieEtablissement || ''} ${siegeData.indiceRepetitionEtablissement || ''} ${siegeData.typeVoieEtablissement || ''} ${siegeData.libelleVoieEtablissement || ''}`.replaceAll(/\s+/g, ' ').trim();

            return {
              ...subcontractor,
              name: companyName,
              siren,
              address: {
                street: formattedStreet,
                postalCode: siegeData.codePostalEtablissement || '',
                city: siegeData.libelleCommuneEtablissement || '',
              },
            } as Subcontractor;
          } catch (err) {
            console.error(`Error enriching subcontractor ${subcontractor.id}:`, err);
            return {
              ...subcontractor,
              name: 'Nom non disponible',
              siren: subcontractor.siret.substring(0, 9),
              address: { street: 'Adresse non disponible', postalCode: '', city: '' },
            } as Subcontractor;
          }
        }),
      );

      setSubcontractors(enrichedSubcontractors);
      setStatus('IDLE');
    } catch (err) {
      console.error('Error fetching subcontractors :', err);
      setErrorMessage('Une erreur est survenue lors de la récupération des agences partenaires');
      setStatus('ERROR');
    }
  };

  // Load subcontractors on mount if needed
  useEffect(() => {
    if (company?.id && (formData.partnerAgenciesCount ?? 0) > 0) {
      fetchSubcontractors();
    }
     
  }, [company?.id]);

  // Auto-validate the setup step when workflow conditions are met
  useEffect(() => {
    const alreadyValidated = !!company?.setupSteps?.transactionQualification;
    const companyId = company?.id;
    if (!companyId || alreadyValidated) { return; }

    const targetCount = formData.partnerAgenciesCount ?? null;
    const noAgenciesCompletion = targetCount === 0;
    const agenciesCompletion = typeof targetCount === 'number' && targetCount > 0
      ? (subcontractors.length >= targetCount && subcontractors.every(s => s.cnilContractSigned))
      : false;

    if (noAgenciesCompletion || agenciesCompletion) {
      updateSetupState(company, jwt, 'transactionQualification', dispatch);
    }
  }, [company, dispatch, jwt, formData.partnerAgenciesCount, subcontractors]);

  const savePartialState = async(partial: Partial<TransactionQualification> = {}) => {
    const nextPayload = buildQualificationPayload(partial);
    setFormData(nextPayload);
    try {
      const completeFormData = {
        hasCarteT: nextPayload.hasCarteT === null ? null : nextPayload.hasCarteT ? 1 : 0,
        willSignMandates: nextPayload.willSignMandates === null ? null : nextPayload.willSignMandates ? 1 : 0,
        partnerAgenciesCount: nextPayload.partnerAgenciesCount,
        transactionEmployees: nextPayload.transactionEmployees ?? [],
        transactionAgents: nextPayload.transactionAgents ?? [],
        gestionLocativeEmployees: nextPayload.gestionLocativeEmployees ?? [],
      };

      const response = await fetch(`/api/companies/${company?.id}/transaction-qualification`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': JWTAuthorization(jwt) },
        body: JSON.stringify(completeFormData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Save error :', errorData);
        throw new Error(errorData.message || 'Failed to save state');
      }

      await updateSetupState(company, jwt, 'transactionQualification', dispatch);

      if (company) {
        dispatch(setSelectedCompany({ ...company, transactionQualification: nextPayload }));
      }

      showToast('success', 'Vos modifications ont été enregistrées avec succès.');
    } catch (err) {
      console.error('Save error :', err);
      showToast('error', 'Une erreur est survenue lors de la sauvegarde de votre progression.');
    }
  };

  // Initialize from company state
  useEffect(() => {
    if (company?.transactionQualification) {
      const transformedData = {
        ...company.transactionQualification,
        partnerAgenciesCount: company.transactionQualification.partnerAgenciesCount ?? null,
        transactionEmployees: company.transactionQualification.transactionEmployees ?? [],
        transactionAgents: company.transactionQualification.transactionAgents ?? [],
        gestionLocativeEmployees: company.transactionQualification.gestionLocativeEmployees ?? [],
      } as TransactionQualification;
      setFormData(transformedData);

      const txAgents = (transformedData.transactionAgents ?? []).map((entry: TransactionAgentRecord) => ({
        userId: typeof entry?.userId === 'number' ? entry.userId : null,
      }));
      setAgents(txAgents);
      setHasAgents(txAgents.length > 0);
      setAgentCount(txAgents.length);
    }
  }, [company?.id]);

  // Handle pending reduction confirmation
  useEffect(() => {
    if (!pendingReduction) {
      setShowReductionDialog(false);
      return;
    }

    setShowReductionDialog(true);
  }, [pendingReduction]);

  const handleReductionConfirm = () => {
    if (!pendingReduction) {
      return;
    }

    const { reason, nextCount } = pendingReduction;

    if (reason === 'count') {
      const trimmedAgents = agents.slice(0, nextCount);
      setAgents(trimmedAgents);
      setAgentCount(nextCount);
      const transactionAgents = trimmedAgents
        .filter((agent) => typeof agent.userId === 'number' && !Number.isNaN(agent.userId))
        .map((agent) => ({ userId: agent.userId as number }));
      setFormData((prev) => ({
        ...prev,
        transactionAgents,
      }));
      savePartialState({ transactionAgents });
    } else if (reason === 'toggle') {
      setHasAgents(false);
      setAgentCount(0);
      setAgents([]);
      setFormData((prev) => ({ ...prev, transactionAgents: [] }));
      savePartialState({ transactionAgents: [] });
    }

    setPendingReduction(null);
    setShowReductionDialog(false);
  };

  const handleReductionCancel = () => {
    if (pendingReduction) {
      const { reason, previousCount } = pendingReduction;

      if (reason === 'count') {
        setAgentCount(previousCount);
      }
    }

    setPendingReduction(null);
    setShowReductionDialog(false);
  };

  // Handlers for persisted sections
  const handleCarteChange = async(value: boolean) => {
    await savePartialState({ hasCarteT: value, willSignMandates: null, partnerAgenciesCount: null });
  };

  const handleMandateSigningChange = async(value: boolean) => {
    await savePartialState({ willSignMandates: value, partnerAgenciesCount: null });
  };

  const handleAgencyCountChange = async(count: number) => {
    await savePartialState({ partnerAgenciesCount: count });
  };

  // Subcontractor actions
  const handleAddSiren = async() => {
    if (!company?.id) {return;}
    if (!/^\d{14}$/.test(siretInput)) {
      setErrorMessage('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }
    if (subcontractors.some(subcontractor => subcontractor.siret === siretInput)) {
      setErrorMessage('Cette agence a déjà été ajoutée à la liste');
      return;
    }

    setStatus('PENDING');
    try {
      const siren = siretInput.substring(0, 9);
      const siegeResponse = await fetch(`/api/sirens/${siren}`, { headers: { 'Authorization': JWTAuthorization(jwt) } });
      if (!siegeResponse.ok) { throw new Error('Failed to get headquarters information'); }
      const siegeData = await siegeResponse.json();

      const siretResponse = await fetch(`/api/sirens/${siretInput}`, { headers: { 'Authorization': JWTAuthorization(jwt) } });
      if (!siretResponse.ok) { throw new Error('Failed to get establishment information'); }
      const siretData = await siretResponse.json();

      const createResponse = await fetch(`/api/companies/${company.id}/subcontractors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': JWTAuthorization(jwt) },
        body: JSON.stringify({ siret: siretInput }),
      });
      if (!createResponse.ok) { throw new Error('Failed to create subcontractor'); }
      const newSubcontractor = await createResponse.json();

      const companyName =
        siegeData.denominationUsuelleEtablissement ||
        siegeData.enseigne1Etablissement ||
        siretData.denominationUsuelleEtablissement ||
        siretData.enseigne1Etablissement ||
        siegeData.denominationUniteLegale ||
        siretData.denominationUniteLegale ||
        siegeData.nomUniteLegale ||
        siretData.nomUniteLegale ||
        'Nom non disponible';

      const formattedStreet = `${siegeData.numeroVoieEtablissement || ''} ${siegeData.indiceRepetitionEtablissement || ''} ${siegeData.typeVoieEtablissement || ''} ${siegeData.libelleVoieEtablissement || ''}`.replaceAll(/\s+/g, ' ').trim();

      const enrichedSubcontractor: Subcontractor = {
        ...newSubcontractor,
        name: companyName,
        siren,
        address: {
          street: formattedStreet,
          postalCode: siegeData.codePostalEtablissement || '',
          city: siegeData.libelleCommuneEtablissement || '',
        },
      };

      setSubcontractors([...subcontractors, enrichedSubcontractor]);
      setSiretInput('');
      setErrorMessage(null);
    } catch (err) {
      console.error('Error adding subcontractor :', err);
      setErrorMessage('Une erreur est survenue lors de la vérification du SIRET');
    } finally {
      setStatus('IDLE');
    }
  };

  const handleDeleteSiren = async(subcontractorId: number) => {
    if (!company?.id) {return;}
    try {
      setStatus('PENDING');
      const response = await fetch(`/api/subcontractors/${subcontractorId}`, { method: 'DELETE', headers: { 'Authorization': JWTAuthorization(jwt) } });
      if (!response.ok) { throw new Error('Failed to delete subcontractor'); }
      setSubcontractors(subcontractors.filter(s => s.id !== subcontractorId));
      setStatus('IDLE');
    } catch (err) {
      console.error('Error deleting subcontractor :', err);
      setErrorMessage("Une erreur est survenue lors de la suppression de l'agence partenaire");
      setStatus('ERROR');
    }
  };

  const handleContractSigned = async(subcontractorId: number) => {
    if (!company?.id) {return;}
    try {
      setStatus('PENDING');
      const response = await fetch(`/api/subcontractors/${subcontractorId}/cnil-contract`, { method: 'PATCH', headers: { 'Authorization': JWTAuthorization(jwt) } });
      if (!response.ok) { throw new Error('Failed to mark contract as signed'); }
      setSubcontractors(subcontractors.map(s => s.id === subcontractorId ? { ...s, cnilContractSigned: true } : s));
      setStatus('IDLE');
    } catch (err) {
      console.error('Error marking contract as signed :', err);
      setErrorMessage('Une erreur est survenue lors de la signature du contrat de sous-traitance');
      setStatus('ERROR');
    }
  };

  // Save button handler (finalize + navigate)
  const handleSubmit = async() => {
    // Validate subcontractors section if agencies are expected
    const targetCount = formData.partnerAgenciesCount ?? 0;
    if (targetCount > 0) {
      if (subcontractors.length < targetCount) {
        showToast('error', 'Veuillez entrer toutes les agences partenaires.');
        setErrorMessage('Veuillez entrer toutes les agences partenaires.');
        return;
      }
      const allSigned = subcontractors.every(subcontractor => subcontractor.cnilContractSigned);
      if (!allSigned) {
        showToast('error', 'Veuillez signer le contrat de sous-traitance pour toutes les agences partenaires avant de continuer.');
        setErrorMessage('Veuillez signer le contrat de sous-traitance pour toutes les agences partenaires avant de continuer.');
        return;
      }
    }

    setStatus('PENDING');
    try {
      const payload = buildQualificationPayload();
      const completeFormData = {
        hasCarteT: payload.hasCarteT === null ? null : payload.hasCarteT ? 1 : 0,
        willSignMandates: payload.willSignMandates === null ? null : payload.willSignMandates ? 1 : 0,
        partnerAgenciesCount: payload.partnerAgenciesCount,
        transactionEmployees: payload.transactionEmployees ?? [],
        transactionAgents: payload.transactionAgents ?? [],
        gestionLocativeEmployees: payload.gestionLocativeEmployees ?? [],
      };

      const response = await fetch(`/api/companies/${company?.id}/transaction-qualification`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': JWTAuthorization(jwt) },
        body: JSON.stringify(completeFormData),
      });
      
      if (!response.ok) { throw new Error('Failed to save transaction qualification'); }

      await updateSetupState(company, jwt, 'transactionQualification', dispatch);
      setFormData(payload);
      setStatus('SUCCESS');
      navigate(ROUTES.SETUP_BRANDING);
    } catch {
      setStatus('ERROR');
      showToast('error', 'Une erreur est survenue lors de la sauvegarde des données.');
    }
  };

  const addAgent = () => {
    if (agents.length < agentCount) {
      const nextAgents = [...agents, { userId: null }];
      setAgents(nextAgents);
      const transactionAgents = nextAgents
        .filter((agent) => typeof agent.userId === 'number' && !Number.isNaN(agent.userId))
        .map((agent) => ({ userId: agent.userId as number }));
      setFormData((prev) => ({
        ...prev,
        transactionAgents,
      }));
      // Don't auto-save when adding - agent has no userId yet
    }
  };
  const updateAgent = (index: number, userId: number | null) => {
    const nextAgents = [...agents];
    nextAgents[index] = { userId };
    setAgents(nextAgents);
    const transactionAgents = nextAgents
      .filter((agent) => typeof agent.userId === 'number' && !Number.isNaN(agent.userId))
      .map((agent) => ({ userId: agent.userId as number }));
    setFormData((prev) => ({
      ...prev,
      transactionAgents,
    }));
    savePartialState({ transactionAgents });
  };
  const removeAgent = (index: number) => {
    const nextAgents = agents.filter((_, i) => i !== index);
    setAgents(nextAgents);
    const transactionAgents = nextAgents
      .filter((agent) => typeof agent.userId === 'number' && !Number.isNaN(agent.userId))
      .map((agent) => ({ userId: agent.userId as number }));
    setFormData((prev) => ({
      ...prev,
      transactionAgents,
    }));
    savePartialState({ transactionAgents });
  };

  const content = !company?.id ? (
    <div className="flex min-h-[200px] flex-col items-center justify-center">
      <Spinner aria-label="Loading..." size="xl" />
      <p className="mt-4 text-gray-600">Chargement des données en cours...</p>
    </div>
  ) : (
    <div className="space-y-8">
      
      {!hideIntro && (
        <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <p className="mb-6 text-lg leading-relaxed font-medium text-gray-800">
              Cette étape permet de qualifier votre activité de transaction immobilière et de configurer vos partenariats.
              <br />
              <span className="font-semibold">
                Pour une gestion optimale, <span className="text-rgpd-blue-light">veuillez renseigner les informations suivantes :</span>
              </span>
            </p>
            <div className="mt-8 border-t border-gray-100 pt-6">
              <h2 className="flex flex-col items-start gap-3 text-xl font-bold text-gray-800 md:flex-row md:items-center">
                <span className="rounded bg-rgpd-blue-light px-3 py-1 text-sm tracking-wide whitespace-nowrap text-white uppercase">ÉTAPE N°4 :</span>
                <span>QUALIFICATION TRANSACTION & GESTION LOCATIVE</span>
              </h2>
            </div>
        </div>
      )}

      <div className="mx-0 md:mx-14">
        {/* Carte T */}
        <div className="mb-8 border-b border-gray-200 pb-8">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">1 - Avez-vous la carte T ?</h3>
          <Step1CarteT hasCarteT={formData.hasCarteT} onChange={handleCarteChange} />
        </div>

        {/* Mandates */}
        <div className="mb-8 border-b border-gray-200 pb-8">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">2 - Signature des mandats</h3>
          <Step2Mandates willSignMandates={formData.willSignMandates} onChange={handleMandateSigningChange} />
        </div>

        {/* Agents commerciaux (local only) */}
        <AgentsSection
          hasAgents={hasAgents}
          setHasAgents={(value) => {
            if (!value) {
              if (agents.length > 0) {
                setPendingReduction({ section: 'agents', reason: 'toggle', nextCount: 0, previousCount: agentCount });
                return;
              }
              setHasAgents(false);
              setAgentCount(0);
              setAgents([]);
              setFormData((prev) => ({ ...prev, transactionAgents: [] }));
              return;
            }
            setHasAgents(true);
          }}
          agentCount={agentCount}
          setAgentCount={(count) => {
            const normalized = Math.max(0, count);
            const previous = agentCount;

            if (normalized < agents.length) {
              setPendingReduction({ section: 'agents', reason: 'count', nextCount: normalized, previousCount: previous });
              setAgentCount(normalized);
              return;
            }

            setAgentCount(normalized);
          }}
          agents={agents}
          addAgent={addAgent}
          updateAgent={updateAgent}
          removeAgent={removeAgent}
        />

        {/* Partner agencies count */}
        <div className="mb-8 border-b border-gray-200 pb-8">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">4 - Agences partenaires</h3>
          <Step3AgenciesCount hasCarteT={formData.hasCarteT} partnerAgenciesCount={formData.partnerAgenciesCount} onChange={handleAgencyCountChange} />
          
          {/* Agencies management - inline, no extra wrapper */}
          {(formData.partnerAgenciesCount ?? 0) > 0 && (
            <div className="mt-6 border-t border-gray-100 pt-6">
              <Step4AgenciesList
                status={status}
                targetCount={formData.partnerAgenciesCount ?? 0}
                subcontractors={subcontractors}
                siretInput={siretInput}
                errorMessage={errorMessage}
                onSiretChange={setSiretInput}
                onAddSiren={handleAddSiren}
                onDelete={handleDeleteSiren}
                onMarkSigned={handleContractSigned}
              />
            </div>
          )}
        </div>

        {errorMessage && (
          <Badge mode="error" text={errorMessage} className="mt-4" />
        )}

        {!hideActions && (
          <div className="mt-10 flex w-full justify-end text-right md:w-auto">
            <button
              onClick={handleSubmit}
              className={classNames(
                'bg-rgpd-green text-white text-xl px-8 py-4 shadow-md align-center w-full md:w-auto rounded',
                status === 'PENDING' && 'opacity-80 cursor-not-allowed',
              )}
              disabled={status === 'PENDING'}
            >
              {status === 'PENDING' ? (
                <div className="flex items-center gap-2">
                  <Spinner size="sm" />
                  <span>Enregistrement...</span>
                </div>
              ) : (
                'Enregistrer et continuer'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {hideTitle ? (
        <div>{content}</div>
      ) : (
        <SetupPage step="transactionQualification" title="QUALIFICATION TRANSACTION & GESTION LOCATIVE">
          {content}
        </SetupPage>
      )}

      <ConfirmationDialog
        open={showReductionDialog}
        title="Attention : Suppression d'attributions"
        confirmLabel="Continuer et supprimer"
        cancelLabel="Annuler"
        onConfirm={handleReductionConfirm}
        onClose={handleReductionCancel}
        description={
          <p className="text-gray-700">
            Cette action va supprimer des salariés et potentiellement leurs attributions existantes.
            Êtes-vous sûr de vouloir continuer ?
          </p>
        }
      />
    </>
  );
};

export default TransactionQuestionnaire;